import React, { useState, useEffect } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useAuth, ROLES, ROLE_LABELS } from '../../context/AuthContext';
import { TokenService } from '../../services/api';
import ChangePasswordModal from './ChangePasswordModal';
import './Layout.css';

// SVG иконки вынесены в константы для переиспользования
const HomeIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 9.5L12 3l9 6.5V20a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V9.5Z" />
    <path d="M9 22V12h6v10" />
  </svg>
);

const MyTrainingsIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
    <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
  </svg>
);

const EventsIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);

const HeatmapIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 0 0 2.5 2.5z" />
  </svg>
);

const SettingsIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

// Общие пункты меню, вынесенные для переиспользования
const COMMON_MENU_ITEMS = {
  home: {
    path: '/',
    name: 'Главная',
    icon: <HomeIcon />
  },
  myTrainings: {
    path: '/my-trainings',
    name: 'Мои обучения',
    icon: <MyTrainingsIcon />
  },
  events: {
    path: '/events',
    name: 'Мероприятия',
    icon: <EventsIcon />
  },
  heatmap: {
    path: '/heatmap',
    name: 'Тепловая карта',
    icon: <HeatmapIcon />
  },
  settings: {
    path: '/settings',
    name: 'Справочники',
    icon: <SettingsIcon />
  },
  dictionaries: {
    path: '/settings',
    name: 'Справочники',
    icon: <SettingsIcon />
  }
};

const Layout = () => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const { user, logout, userRole, loading, isAuthenticated, refreshUserData } = useAuth();
  const navigate = useNavigate();
  const [isPersistent, setIsPersistent] = useState(false);

  // Проверяем тип сессии
  useEffect(() => {
    setIsPersistent(TokenService.isPersistentSession());
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleOpenPasswordModal = () => {
    setIsPasswordModalOpen(true);
  };

  const handlePasswordChangeSuccess = () => {
    // Можно добавить уведомление при необходимости
  };

  // Формирование меню в зависимости от роли
  const getMenuItems = () => {
    const items = [];

    // Если роль еще не загружена или пользователь не авторизован, показываем только заглушку
    if (loading || !isAuthenticated || !userRole) {
      return items;
    }

    // Главная страница - для всех ролей
    items.push(COMMON_MENU_ITEMS.home);

    // === ДЛЯ ПОЛЬЗОВАТЕЛЯ (user) ===
    if (userRole === ROLES.USER) {
      items.push(COMMON_MENU_ITEMS.myTrainings);
      return items;
    }

    // Общие пункты для HR_MANAGER, TRAINING_CURATOR, ADMIN
    items.push(COMMON_MENU_ITEMS.events);
    items.push(COMMON_MENU_ITEMS.heatmap);

    // === ДЛЯ HR-МЕНЕДЖЕРА (hr_manager) ===
    if (userRole === ROLES.HR_MANAGER) {
      items.push(COMMON_MENU_ITEMS.settings);
      return items;
    }

    // === ДЛЯ КУРАТОРА ОБУЧЕНИЯ (training_curator) ===
    if (userRole === ROLES.TRAINING_CURATOR) {
      items.push(COMMON_MENU_ITEMS.dictionaries);
      return items;
    }

    // === ДЛЯ АДМИНИСТРАТОРА (admin) ===
    if (userRole === ROLES.ADMIN) {
      items.push(COMMON_MENU_ITEMS.settings);
      return items;
    }

    return items;
  };

  const menuItems = getMenuItems();

  // Получение названия роли для отображения
  const getRoleDisplayName = () => {
    return ROLE_LABELS[userRole] || 'Пользователь';
  };

  // Обновление данных пользователя
  const handleRefreshUser = async () => {
    if (refreshUserData) {
      await refreshUserData();
    }
  };

  // Показываем загрузку, пока роль не определена
  if (loading) {
    return (
      <div className="layout">
        <aside className="sidebar">
          <div className="sidebar-header">
            <div className="logo">
              <span className="logo-text">Управление</span>
            </div>
          </div>
          <div className="sidebar-nav loading">
            <p>Загрузка...</p>
          </div>
        </aside>
        <main className="main-content">
          <div className="loading-container">
            <p>Загрузка данных...</p>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className={`layout ${isCollapsed ? 'collapsed' : ''}`}>
      <aside className="sidebar">
        <div className="sidebar-header">
          <div className="logo">
            {!isCollapsed && <span className="logo-text">Управление</span>}
            <button className="toggle-btn" onClick={() => setIsCollapsed(!isCollapsed)}>
              {isCollapsed ? '→' : '←'}
            </button>
          </div>
        </div>

        <nav className="sidebar-nav">
          {menuItems.length > 0 ? (
            menuItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
              >
                <span className="nav-icon" style={{ marginRight: !isCollapsed ? '10px' : '0' }}>{item.icon}</span>
                {!isCollapsed && <span className="nav-text">{item.name}</span>}
              </NavLink>
            ))
          ) : (
            <div className="no-menu-items">Нет доступных разделов</div>
          )}
        </nav>

        {!isCollapsed && (
          <div className="sidebar-footer">
            <div className="user-info">
              <div className="user-avatar" onClick={handleOpenPasswordModal} style={{ cursor: 'pointer' }}>
                {user?.avatar || '👤'}
              </div>
              <div className="user-details">
                <div className="user-name">{user?.name || user?.username || 'Пользователь'}</div>
                <div className="user-role">{getRoleDisplayName()}</div>
                {isPersistent && (
                  <div className="session-badge" title="Вы остаетесь авторизованным при закрытии браузера">
                    🔒 Постоянная сессия
                  </div>
                )}
                <button 
                  onClick={handleOpenPasswordModal}
                  className="change-password-link"
                >
                  🔑 Сменить пароль
                </button>
              </div>
            </div>
            <div className="sidebar-actions">
              <button onClick={handleLogout} className="logout-btn">
                🚪 Выйти
              </button>
            </div>
          </div>
        )}
      </aside>

      <main className="main-content">
        <Outlet />
      </main>

      <ChangePasswordModal
        isOpen={isPasswordModalOpen}
        onClose={() => setIsPasswordModalOpen(false)}
        onSuccess={handlePasswordChangeSuccess}
      />
    </div>
  );
};

export default Layout;